import {
  concat,
  camelCase,
  first,
  groupBy,
  lowerCase,
  partition,
  some
} from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';

import {
  DOCUMENT_TYPES,
  TIME_PERIOD,
  PRODUCT_TYPES
} from './StatementsActions';
import {
  DATE_DISPLAY_FORMAT,
  dayDifference,
  isYear,
  CURRENT_YEAR
} from 'services/Dates';
import { sortByDate } from 'services/Sorts';
import { getNonViewOnlyProducts } from 'state/account/AccountSelectors';

import { getCustomerResidentialState } from 'state/customer/CustomerSelectors';

export const ALL_DOCUMENT_TYPES = 'all';
export const STATEMENTS_DOC_SELECTION = 'statement';
export const TAX_FORM_DOC_SELECTION = 'taxform';
export const CONFIRMATION_DOC_SELECTION = 'confirmation';
export const MUTUAL_FUNDS = 'mutualFund';
export const BROKERAGE = 'brokerage';
export const COLLEGE_SAVINGS = 'collegeSavings';
export const ACTIVE_PLUS = 'activePlus';
export const ADVICE_MANAGED = 'adviceManaged';
export const ACTIVE_ADVISORY_SERVICE = 'activeAdvisory';
export const OTHER = 'other';
export const TAX_FORM = 'Tax Form';

const productTypesMap = {
  mutualFund: 10,
  brokerage: 20,
  collegeSavings: [40, 70, 80],
  activePlus: 110,
  activeAdvisory: 120
};

export const CCPA_DOCUMENT_ID = 1121;

const getDocumentReferences = (state = {}) => state.documentReferences;
export const getCcpaDocumentReferences = (state = {}) =>
  state.ccpaDocumentReferences;
export const getStatementFilterState = (state = {}) =>
  state.statementsFilteringSelections;

function documentTypesFilter(document, selection) {
  return (
    camelCase(document.type).toLowerCase() === selection ||
    camelCase(document.name).toLowerCase() === selection ||
    selection === ALL_DOCUMENT_TYPES
  );
}

function timePeriodFilter(document, selection) {
  if (isYear(selection)) {
    return moment.utc(document.date).year() == selection;
  }

  return dayDifference(document.date) >= -Math.abs(selection);
}

export function productTypesFilter(document, selection) {
  let productType = productTypesMap[selection];

  // Other will not have a product type so we will always return true
  if (lowerCase(document.name) === OTHER) {
    return true;
  }
  if (
    (selection === ALL_DOCUMENT_TYPES || selection === BROKERAGE) &&
    (document.productType === 10 || document.productType === 20)
  ) {
    // this necessary because brokerage statements are actually mapped to mutual fund
    return true;
  }
  if (selection === COLLEGE_SAVINGS) {
    return productType.some((product) => product === document.productType);
  }
  return (
    document.productType === productType || selection === ALL_DOCUMENT_TYPES
  );
}

export const getStatementsFilteringSelections = createSelector(
  getStatementFilterState,
  getCustomerResidentialState,
  (filteringState = {}, residentialState) => {
    const {
      documentTypes: { options }
    } = filteringState;
    if (residentialState && residentialState === 'CA' && options) {
      if(!filteringState.documentTypes.options.some(e => e.value === OTHER)){
        filteringState.documentTypes.options.push(
          {
            label: "Other",
            value: OTHER.toLowerCase()
          });
      }
    }
    return filteringState;
  }
);

export const getFilteredDocumentReferences = createSelector(
  getDocumentReferences,
  getStatementFilterState,
  (documents = [], filterState = {}) => {
    const { selection: docSelection = ALL_DOCUMENT_TYPES } =
      filterState[DOCUMENT_TYPES] || {};
    const { selection: timeSelection = CURRENT_YEAR.toString() } =
      filterState[TIME_PERIOD] || {};
    const { selection: productSelection = ALL_DOCUMENT_TYPES } =
      filterState[PRODUCT_TYPES] || {};
    const filteredDocuments = documents.reduce((accum, current) => {
      if (documentTypesFilter(current, docSelection)) {
        if (timePeriodFilter(current, timeSelection)) {
          if (productTypesFilter(current, productSelection)) {
            accum.push(current);
          }
        }
      }
      return accum;
    }, []);
    const [taxForms, otherDocuments] = partition(
      filteredDocuments,
      (x) => x.type === TAX_FORM
    );
    taxForms.sort((a, b) => sortByDate(a, b));
    otherDocuments.sort((a, b) => sortByDate(a, b));
    return concat(taxForms, otherDocuments);
  }
);

export const getMobileDocumentGroupsObject = createSelector(
  getFilteredDocumentReferences,
  (documents = []) => {
    const taxDocuments = [];
    const otherDocuments = [];
    documents.forEach((doc) => {
      if (doc.type === TAX_FORM) {
        taxDocuments.push(doc);
      } else {
        otherDocuments.push(doc);
      }
    });
    const taxDocumentsGroup = groupBy(taxDocuments, (taxDoc) => taxDoc.date);
    const otherDocumentsGroup = groupBy(
      otherDocuments,
      (otherDoc) => otherDoc.date
    );
    return { ...taxDocumentsGroup, ...otherDocumentsGroup };
  }
);

export const getMobileDocumentKeysGroupsObject = createSelector(
  getMobileDocumentGroupsObject,
  (documentGroups = {}) => {
    const keys = Object.keys(documentGroups);

    return {
      documentGroups,
      keys
    };
  }
);

export const getMobileFilterDocumentReferencesGroups = createSelector(
  getMobileDocumentKeysGroupsObject,
  ({ documentGroups = {}, keys = [] }) => {
    return keys.map((key) => {
      // tax form groups are displayed differently [YYYY] vs [DD/MM/YYYY]
      const hasOtherForm =
        documentGroups[key] && first(documentGroups[key]).type !== TAX_FORM;
      const dateKey = hasOtherForm
        ? moment.utc(key).format(DATE_DISPLAY_FORMAT)
        : moment.utc(key).year();

      return {
        [dateKey]: documentGroups[key]
      };
    });
  }
);

/**
 * @param Array nonViewOnlyProducts
 * @returns Array of objects for product type dropdown options
 */
export const getNonViewOnlyProductTypeOptions = createSelector(
  getNonViewOnlyProducts,
  (products) => {
    const options = [];
    options.push({ label: 'All', value: ALL_DOCUMENT_TYPES });
    products.forEach((product) => {
      if (
        product === MUTUAL_FUNDS &&
        !some(options, (x) => x.value === MUTUAL_FUNDS)
      ) {
        options.push({ label: 'Mutual Funds', value: MUTUAL_FUNDS });
      }
      if (
        product === ACTIVE_PLUS &&
        !some(options, (x) => x.value === ACTIVE_PLUS)
      ) {
        options.push({ label: 'ActivePlus Portfolios', value: ACTIVE_PLUS });
      }
      if (
        product === ACTIVE_ADVISORY_SERVICE &&
        !some(options, (x) => x.value === ACTIVE_ADVISORY_SERVICE)
      ) {
        options.push({
          label: 'Retirement Advisory Service',
          value: ACTIVE_ADVISORY_SERVICE
        });
      }
      if (
        product === BROKERAGE &&
        !some(options, (x) => x.value === BROKERAGE)
      ) {
        options.push({ label: 'Brokerage', value: BROKERAGE });
      }
      if (
        (product === 'maryland529' ||
          product === 'national529' ||
          product === 'alaska529') &&
        !some(options, (x) => x.value === COLLEGE_SAVINGS)
      ) {
        options.push({
          label: '529 College Savings Plans',
          value: COLLEGE_SAVINGS
        });
      }
    });
    let sortOrder = [
      ALL_DOCUMENT_TYPES,
      MUTUAL_FUNDS,
      ACTIVE_PLUS,
      ACTIVE_ADVISORY_SERVICE,
      BROKERAGE,
      COLLEGE_SAVINGS
    ];
    return options.sort((a, b) => {
      return sortOrder.indexOf(a.value) - sortOrder.indexOf(b.value);
    });
  }
);
